<template>
  <transition name="home-coupon-modal-fade">
    <div v-if="visible" class="home-coupon-modal">
      <div class="home-coupon-modal__mask" @click="closeModal"></div>

      <div class="home-coupon-modal__dialog" @click.stop>
        <button class="home-coupon-modal__close" type="button" @click="closeModal">
          <i class="fa fa-times"></i>
        </button>

        <div class="home-coupon-modal__header">
          <p class="home-coupon-modal__eyebrow">{{ $t("header.couponCenterTitle") }}</p>
          <h2 class="home-coupon-modal__title">{{ $t("indexPage.coupon.headerTitle") }}</h2>
          <p class="home-coupon-modal__subtitle">{{ $t("indexPage.coupon.headerSubtitle") }}</p>

          <div class="home-coupon-modal__decoration home-coupon-modal__decoration--left"></div>
          <div class="home-coupon-modal__decoration home-coupon-modal__decoration--right"></div>
        </div>

        <div class="home-coupon-modal__body" v-loading="loading">
          <div v-if="claimableCampaignList.length" class="home-coupon-modal__list">
            <article
              v-for="campaign in claimableCampaignList"
              :key="campaign.id"
              class="campaign-ticket"
            >
              <div class="campaign-ticket__amount">
                <div class="campaign-ticket__amount-value">{{ formatDiscountValue(campaign) }}</div>
                <div class="campaign-ticket__amount-label">{{ formatDiscountLabel(campaign) }}</div>
              </div>

              <div class="campaign-ticket__divider"></div>

              <div class="campaign-ticket__content">
                <div class="campaign-ticket__top">
                  <div class="campaign-ticket__title">{{ localizeName(campaign) }}</div>
                  <span class="campaign-ticket__badge">{{ $t("couponHall.statusAvailable") }}</span>
                </div>

                <div class="campaign-ticket__meta">
                  {{ $t("couponHall.goodsLabel") }}: {{ formatGoodsName(campaign) }}
                </div>
                <div class="campaign-ticket__meta">
                  {{ $t("couponHall.conditionLabel") }}: {{ formatCondition(campaign) }}
                </div>
                <div class="campaign-ticket__meta">
                  {{ $t("couponHall.validityLabel") }}: {{ formatValidity(campaign) }}
                </div>

                <button
                  class="campaign-ticket__action"
                  type="button"
                  :disabled="isActionDisabled(campaign)"
                  @click="handleAction(campaign)"
                >
                  <span>{{ resolveActionText(campaign) }}</span>
                </button>
              </div>
            </article>
          </div>

          <div v-else-if="!loading" class="home-coupon-modal__empty">
            <div class="home-coupon-modal__empty-title">{{ $t("couponHall.emptyTitle") }}</div>
            <p class="home-coupon-modal__empty-subtitle">{{ $t("couponHall.emptySubtitle") }}</p>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { getCouponCampaignList, receiveCouponCampaign } from "@/api/user";
import { getToken } from "@/utils/auth";

export default {
  name: "HomeCouponCampaignModal",
  data() {
    return {
      visible: false,
      loading: false,
      receivingId: null,
      campaignList: [],
    };
  },
  computed: {
    isLoggedIn() {
      return Boolean(this.$store.state.user.token || getToken());
    },
    claimableCampaignList() {
      return this.campaignList.filter((campaign) => Number(campaign.receiveStatus) === 0);
    },
  },
  mounted() {
    this.tryAutoOpen();
  },
  methods: {
    async tryAutoOpen() {
      await this.loadCampaigns();
      if (!this.claimableCampaignList.length) {
        return;
      }

      this.visible = true;
    },
    closeModal() {
      this.visible = false;
      // 记录弹窗已显示，同 session 不再弹出
      sessionStorage.setItem('homeCouponModalShown', '1');
    },
    async loadCampaigns() {
      this.loading = true;
      try {
        const res = await getCouponCampaignList();
        if (res && res.code === 200) {
          this.campaignList = Array.isArray(res.data) ? res.data : [];
          return;
        }
        this.campaignList = [];
      } catch (error) {
        this.campaignList = [];
      } finally {
        this.loading = false;
      }
    },
    resolveActionText(campaign) {
      if (!campaign) {
        return this.$t("couponHall.receiveNow");
      }
      if (!this.isLoggedIn && Number(campaign.receiveStatus) === 0) {
        return this.$t("couponHall.loginToReceive");
      }
      return this.$t("couponHall.receiveNow");
    },
    isActionDisabled(campaign) {
      if (!campaign) {
        return true;
      }
      return this.receivingId === campaign.id || Number(campaign.receiveStatus) !== 0;
    },
    async handleAction(campaign) {
      if (!campaign) {
        return;
      }
      if (!this.isLoggedIn) {
        this.goLogin();
        return;
      }
      if (this.isActionDisabled(campaign)) {
        return;
      }

      this.receivingId = campaign.id;
      try {
        const res = await receiveCouponCampaign({ campaignId: campaign.id });
        if (res && res.code === 200) {
          this.$message.success(this.$t("couponHall.receiveSuccess"));
          await this.loadCampaigns();
          return;
        }

        await this.loadCampaigns();
        this.$message.error(this.resolveReceiveMessage(res));
      } catch (error) {
        await this.loadCampaigns();
        this.$message.error(this.$t("couponHall.receiveFailed"));
      } finally {
        this.receivingId = null;
      }
    },
    resolveReceiveMessage(res) {
      const msg = res && res.msg ? String(res.msg) : "";
      if (!msg) {
        return this.$t("couponHall.receiveFailed");
      }
      const messageMap = {
        "Campaign has not started yet": this.$t("couponHall.statusUpcoming"),
        "Campaign has already ended": this.$t("couponHall.statusEnded"),
        "Coupons are sold out": this.$t("couponHall.statusSoldOut"),
        "Receive limit reached": this.$t("couponHall.statusLimitReached"),
      };
      return messageMap[msg] || msg || this.$t("couponHall.receiveFailed");
    },
    localizeName(campaign) {
      if (this.$i18n.locale === "en" && campaign.nameEn) {
        return campaign.nameEn;
      }
      if (this.$i18n.locale === "ko" && campaign.nameKo) {
        return campaign.nameKo;
      }
      return campaign.name;
    },
    formatDiscountValue(campaign) {
      const discountType = Number(campaign.discountType);
      if (discountType === 3) {
        return `${Math.round((1 - this.toNumber(campaign.discountRate, 1)) * 100)}%`;
      }
      if (discountType === 2) {
        return `$${this.formatAmount(campaign.discountAmount)}`;
      }
      return `$${this.formatAmount(campaign.money)}`;
    },
    formatDiscountLabel(campaign) {
      if (Number(campaign.discountType) === 3) {
        return this.$t("coupon.directDiscount");
      }
      return this.$t("indexPage.coupon.directDiscount");
    },
    formatCondition(campaign) {
      const discountType = Number(campaign.discountType);
      if (discountType === 1) {
        const threshold = this.toNumber(campaign.price);
        const discount = `$${this.formatAmount(campaign.money)}`;
        if (threshold <= 0) {
          return `${this.$t("coupon.noThreshold")} / ${discount}`;
        }
        return `${this.$t("coupon.minSpend")} $${this.formatAmount(threshold)} / ${discount}`;
      }
      if (discountType === 2) {
        return `$${this.formatAmount(campaign.discountAmount)}`;
      }
      return `${Math.round((1 - this.toNumber(campaign.discountRate, 1)) * 100)}% OFF`;
    },
    formatValidity(campaign) {
      if (Number(campaign.validType) === 1) {
        return this.formatDateTime(campaign.fixedValidTime);
      }
      return this.$t("coupon.validForDays", {
        days: this.toNumber(campaign.validDays, 1),
      });
    },
    formatGoodsName(campaign) {
      if (!campaign || this.toNumber(campaign.goodsId) <= 0) {
        return this.$t("couponHall.allGoods");
      }
      return campaign.goodsName || this.$t("couponHall.allGoods");
    },
    formatDateTime(dateInput) {
      if (!dateInput) {
        return "-";
      }
      const date = new Date(dateInput);
      if (Number.isNaN(date.getTime())) {
        return "-";
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatAmount(value) {
      return this.toNumber(value).toFixed(2);
    },
    toNumber(value, fallback = 0) {
      const numberValue = Number(value);
      return Number.isFinite(numberValue) ? numberValue : fallback;
    },
    goLogin() {
      this.$router.push({
        path: "/login",
        query: { redirect: this.$route.fullPath },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.home-coupon-modal {
  position: fixed;
  inset: 0;
  z-index: 2400;
}

.home-coupon-modal__mask {
  position: absolute;
  inset: 0;
  background: rgba(7, 16, 33, 0.56);
  backdrop-filter: blur(2px);
}

.home-coupon-modal__dialog {
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(460px, calc(100vw - 32px));
  max-height: min(78vh, 720px);
  transform: translate(-50%, -50%);
  border-radius: 24px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 30px 70px rgba(6, 24, 58, 0.28);
}

.home-coupon-modal__close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.16);
  color: #ffffff;
  cursor: pointer;
}

.home-coupon-modal__header {
  position: relative;
  padding: 28px 24px 24px;
  background: linear-gradient(135deg, #2d67f6 0%, #1852e2 100%);
  color: #ffffff;
  text-align: center;
  overflow: hidden;
}

.home-coupon-modal__eyebrow {
  position: relative;
  z-index: 1;
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.78);
}

.home-coupon-modal__title {
  position: relative;
  z-index: 1;
  margin: 0;
  font-size: clamp(1.5rem, 5vw, 2rem);
  font-weight: 800;
}

.home-coupon-modal__subtitle {
  position: relative;
  z-index: 1;
  margin: 10px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.92);
}

.home-coupon-modal__decoration {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}

.home-coupon-modal__decoration--left {
  top: -18px;
  left: -18px;
  width: 54px;
  height: 54px;
}

.home-coupon-modal__decoration--right {
  right: -14px;
  bottom: -28px;
  width: 96px;
  height: 96px;
}

.home-coupon-modal__body {
  max-height: calc(min(78vh, 720px) - 132px);
  padding: 20px 20px 22px;
  overflow-y: auto;
  background:
    linear-gradient(180deg, rgba(240, 246, 255, 0.72), rgba(255, 255, 255, 0.96));
}

.home-coupon-modal__list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.campaign-ticket {
  display: grid;
  grid-template-columns: 110px 1px minmax(0, 1fr);
  gap: 18px;
  align-items: stretch;
  padding: 16px;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 12px 28px rgba(22, 93, 255, 0.08);
  border: 1px solid rgba(22, 93, 255, 0.08);
}

.campaign-ticket__amount {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.campaign-ticket__amount-value {
  font-size: 30px;
  font-weight: 800;
  line-height: 1;
  color: #1b57ea;
}

.campaign-ticket__amount-label {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #6b7c93;
}

.campaign-ticket__divider {
  position: relative;
  background: linear-gradient(180deg, rgba(41, 95, 240, 0), rgba(41, 95, 240, 0.35), rgba(41, 95, 240, 0));
}

.campaign-ticket__divider::before,
.campaign-ticket__divider::after {
  content: "";
  position: absolute;
  left: 50%;
  width: 14px;
  height: 14px;
  background: #eef4ff;
  border-radius: 50%;
  transform: translateX(-50%);
}

.campaign-ticket__divider::before {
  top: -7px;
}

.campaign-ticket__divider::after {
  bottom: -7px;
}

.campaign-ticket__content {
  min-width: 0;
}

.campaign-ticket__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.campaign-ticket__title {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.45;
  color: #152338;
}

.campaign-ticket__badge {
  flex: none;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(22, 163, 74, 0.12);
  color: #15803d;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

.campaign-ticket__meta {
  font-size: 12px;
  line-height: 1.6;
  color: #66758f;
}

.campaign-ticket__meta + .campaign-ticket__meta {
  margin-top: 4px;
}

.campaign-ticket__action {
  min-width: 120px;
  margin-top: 14px;
  padding: 10px 18px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #2d67f6 0%, #1d56e8 100%);
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.campaign-ticket__action:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(29, 86, 232, 0.22);
}

.campaign-ticket__action:disabled {
  opacity: 0.72;
  cursor: not-allowed;
  box-shadow: none;
}

.home-coupon-modal__empty {
  padding: 34px 20px 30px;
  text-align: center;
}

.home-coupon-modal__empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #17263c;
}

.home-coupon-modal__empty-subtitle {
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.7;
  color: #6b7c93;
}

.home-coupon-modal-fade-enter-active,
.home-coupon-modal-fade-leave-active {
  transition: opacity 0.24s ease, transform 0.24s ease;
}

.home-coupon-modal-fade-enter,
.home-coupon-modal-fade-leave-to {
  opacity: 0;
}

@media only screen and (max-width: 768px) {
  .home-coupon-modal__dialog {
    width: calc(100vw - 24px);
    max-height: min(84vh, 760px);
  }

  .home-coupon-modal__header {
    padding: 24px 18px 20px;
  }

  .home-coupon-modal__body {
    max-height: calc(min(84vh, 760px) - 122px);
    padding: 16px 14px 18px;
  }

  .campaign-ticket {
    grid-template-columns: 1fr;
    gap: 14px;
    padding: 14px;
  }

  .campaign-ticket__divider {
    width: 100%;
    height: 1px;
  }

  .campaign-ticket__divider::before,
  .campaign-ticket__divider::after {
    top: 50%;
    bottom: auto;
    transform: translateY(-50%);
  }

  .campaign-ticket__divider::before {
    left: -7px;
  }

  .campaign-ticket__divider::after {
    left: auto;
    right: -7px;
  }

  .campaign-ticket__top {
    flex-direction: column;
    align-items: flex-start;
  }

  .campaign-ticket__action {
    width: 100%;
  }
}
</style>
